import { action, observable } from "mobx";
import { FormulaFormVM } from "../template/FormulaFormVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";

interface IPressureFlowForm {
    headOnPipe: string;
    waterSurfaceElev: string;
    invertOutletElev: string;
    pipeDiameter: string;
    pipeLength: string;
    manningsN: string;
    entranceLossCoeff: string;
    bendLossCoeff: string;
}
interface IPressureFlowResults {
    flowQ: string | undefined;
    velocity: string | undefined;
    fractionCoefficient: string | undefined;
    maxAllowableFall: string | undefined;
}

export class PressureFlowInPipeVM extends FormulaFormVM {
    readonly formLabelsForExport;
    readonly resultLabelsForExport;
    constructor(props) {
        super(props);
        // Any Form specific contructor code. Otherwise, remove contructor - use parent.

        this.formLabelsForExport = {
            headOnPipe: 'Head on Pipe (ft)',
            waterSurfaceElev: 'Water Surface Elevation (ft)',
            invertOutletElev: 'Invert Outlet Elevation (ft)',
            pipeDiameter: 'Pipe Diameter (in)',
            pipeLength: 'Pipe Length (ft)',
            manningsN: "Manning's n",
            entranceLossCoeff: 'Entrance Loss Coefficient (Ke)',
            bendLossCoeff: 'Bend Loss Coefficient (Kb)'
        }
        this.resultLabelsForExport = {
            flowQ: 'Flow Q (cfs)',
            velocity: 'Velocity (ft/sec)',
            fractionCoefficient: 'Friction Coefficient (Kp)',
            maxAllowableFall: 'Maximum Allowable Fall in Pipe (ft)'
        }
        this.helpLink = "https://usdagcc.sharepoint.com/:b:/r/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/General/NETS-Support%20Documents/Formulas%20Documentation/Help%20Select%20n%20Value.pdf?csf=1&web=1&e=L9CuXP",
        this.helpKeLink = 'https://usdagcc.sharepoint.com/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FFPAC%2DNRCS%2DEngineeringPlanningAnalysisandDesignSoftwareEPAD%2FShared%20Documents%2FGeneral%2FNETS%2DSupport%20Documents%2FFormulas%20Documentation%2FHelp%20with%20Ke%20Value%2Epdf&parent=%2Fsites%2FFPAC%2DNRCS%2DEngineeringPlanningAnalysisandDesignSoftwareEPAD%2FShared%20Documents%2FGeneral%2FNETS%2DSupport%20Documents%2FFormulas%20Documentation'
    }

    @observable pressureFlowForm: IPressureFlowForm = {
        headOnPipe: "0.00",
        waterSurfaceElev: "0.00",
        invertOutletElev: "0.00",
        pipeDiameter: "0.00",
        pipeLength: "0.00",
        manningsN: "0.0000",
        entranceLossCoeff: "0.00",
        bendLossCoeff: "0.00"
    }

    @observable pressureFlowResults: IPressureFlowResults = {
        flowQ: '0',
        velocity: '0',
        fractionCoefficient: '0',
        maxAllowableFall: '0',
    }
    @observable hasResults: boolean = false;
    @observable pressureFlowInputSnapshot: IPressureFlowForm | undefined = undefined;

    @observable specifyControl: 'Head' | 'Elevations' = 'Head';
    @observable specifyControlSnapshot: 'Head' | 'Elevations' = this.specifyControl;
    @observable pressureFlowHeadOnPipeSnapshot: string = "0.00"
    @observable pressureFlowWaterSurfaceElevSnapshot: string = "0.00"

    @observable formValidation = {
        hasError: true,
        headOnPipe: false,
        waterSurfaceElev: false,
        invertOutletElev: false,
        pipeDiameter: false,
        pipeLength: false,
        manningsN: false,
        entranceLossCoeff: false,
        bendLossCoeff: false,
        formErrors: {
            headOnPipe: '',
            waterSurfaceElev: '',
            invertOutletElev: '',
            pipeDiameter: '',
            pipeLength: '',
            manningsN: '',
            entranceLossCoeff: '',
            bendLossCoeff: ''
        }
    };

    @action.bound PressureFlowErrorClass(field) {
        let errorMessage;
        errorMessage = this.formValidation.formErrors[field];
        return errorMessage?.length === 0 ? '' : ' usa-input--error';
    }

    get headControl(): boolean {
        return this.specifyControl === 'Head';
    }
    get elevationsControl(): boolean {
        return this.specifyControl === 'Elevations';
    }
    @action.bound setFixedValue(name, fixed) {
        const event1 = {
         target: {
         name: name,
         value: this.roundingHelperString(fixed, name === "mannings-n"? 4 : 2)
         }
         }
        this.handleFormChange(event1, true);
         }
    @action.bound handleOnKeyDown(event) {
        ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
    }

    @action.bound async handleFormChange(event, within?: boolean) {
        const { name, value } = event.target;
        if (!within && value.length > 7) {
            return
        };
        switch (name) {
            case 'head-on-pipe':
                this.pressureFlowForm.headOnPipe = value;
                break;
            case 'water-surface-elev':
                this.pressureFlowForm.waterSurfaceElev = value;
                break;
            case 'invert-outlet-elev':
                this.pressureFlowForm.invertOutletElev = value;
                break;
            case 'pipe-diameter':
                this.pressureFlowForm.pipeDiameter = value;
                break;
            case 'pipe-length':
                this.pressureFlowForm.pipeLength = value;
                break;
            case 'mannings-n':
                this.pressureFlowForm.manningsN = value;
                break;
            case 'entrance-loss-coeff':
                this.pressureFlowForm.entranceLossCoeff = value;
                break;
            case 'bend-loss-coeff':
                this.pressureFlowForm.bendLossCoeff = value;
                break;
            case 'head':
                this.specifyControl = 'Head';
                break;
            case 'elevations':
                this.specifyControl = 'Elevations';
                break;
        }
    }

    @action.bound async validateInput(name, value) {
        const val = this.roundingHelperNumber(parseFloat(value), name === 'mannings-n' ? 4 : 2);
        this.setFixedValue(name, val);

        switch (name) {
            case 'head-on-pipe':
                if (value === '' || val <= 0) {
                    this.formValidation.headOnPipe = true;
                    this.formValidation.formErrors.headOnPipe = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Common_10', undefined,this.formLabelsForExport.headOnPipe.replace(/(\s\([\w\W]*\))/, ''));
                } else {
                    this.formValidation.headOnPipe = false;
                    this.formValidation.formErrors.headOnPipe = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'water-surface-elev':
                if (value === '' || val <= 0) {
                    this.formValidation.waterSurfaceElev = true;
                    this.formValidation.formErrors.waterSurfaceElev = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Common_10', undefined,this.formLabelsForExport.waterSurfaceElev.replace(/(\s\([\w\W]*\))/, ''));
                } else {
                    this.formValidation.waterSurfaceElev = false;
                    this.formValidation.formErrors.waterSurfaceElev = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'invert-outlet-elev':
                if (value === '' || val < 0) {
                    this.formValidation.invertOutletElev = true;
                    this.formValidation.formErrors.invertOutletElev = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Common_14', undefined, this.formLabelsForExport.invertOutletElev.replace(/(\s\([\w\W]*\))/, ''));
                } else {
                    this.formValidation.invertOutletElev = false;
                    this.formValidation.formErrors.invertOutletElev = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'pipe-diameter':
                if (value === '' || val < 0.02) {
                    this.formValidation.pipeDiameter = true;
                    this.formValidation.formErrors.pipeDiameter = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Formula_07')
                } else {
                    this.formValidation.pipeDiameter = false;
                    this.formValidation.formErrors.pipeDiameter = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'pipe-length':
                if (value === '' || val <= 0) {
                    this.formValidation.pipeLength = true;
                    this.formValidation.formErrors.pipeLength = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.pipeLength.replace(/(\s\([\w\W]*\))/, ''));

                } else {
                    this.formValidation.pipeLength = false;
                    this.formValidation.formErrors.pipeLength = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'mannings-n':
                if (value === '' || val < 0.0001 || val > 1) {
                    this.formValidation.manningsN = true;
                    this.formValidation.formErrors.manningsN = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Formula_02')
                } else {
                    this.formValidation.manningsN = false;
                    this.formValidation.formErrors.manningsN = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'entrance-loss-coeff':
                if (val < 0 || val > 2.0) {
                    this.formValidation.entranceLossCoeff = true;
                    this.formValidation.formErrors.entranceLossCoeff = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Formula_19'); //validate Ke
                } else {
                    this.formValidation.entranceLossCoeff = false;
                    this.formValidation.formErrors.entranceLossCoeff = '';
                    this.formValidation.hasError = false;
                }
                break;
            case 'bend-loss-coeff':
                if (val < 0 || val > 2.0) {
                    this.formValidation.bendLossCoeff = true;
                    this.formValidation.formErrors.bendLossCoeff = ' ';
                    this.formValidation.hasError = true;
                    await MessageCenter.addNotificationFromCode('Formula_20'); //validate Kb
                } else {
                    this.formValidation.bendLossCoeff = false;
                    this.formValidation.formErrors.bendLossCoeff = '';
                    this.formValidation.hasError = false;
                }
                break;
        }
    }

    get validateRequiredInputs() {
        if (
            this.formValidation.headOnPipe ||
            this.formValidation.waterSurfaceElev ||
            this.formValidation.invertOutletElev ||
            this.formValidation.pipeDiameter ||
            this.formValidation.pipeLength ||
            this.formValidation.manningsN ||
            this.formValidation.entranceLossCoeff ||
            this.formValidation.bendLossCoeff
        ) {
            return false;
        }
        return true;
    }

    async validateForm() {
        if (this.specifyControl === 'Head') {
            await this.validateInput('head-on-pipe', this.pressureFlowForm.headOnPipe);
            this.formValidation.waterSurfaceElev = false;
            this.formValidation.formErrors.waterSurfaceElev = '';this.pressureFlowForm.waterSurfaceElev = this.pressureFlowWaterSurfaceElevSnapshot;
            this.formValidation.invertOutletElev = false;
            this.formValidation.formErrors.invertOutletElev = '';this.pressureFlowForm.invertOutletElev = this.pressureFlowInputSnapshot?.invertOutletElev!;
        }
        else {
            await this.validateInput('water-surface-elev', this.pressureFlowForm.waterSurfaceElev);
            await this.validateInput('invert-outlet-elev', this.pressureFlowForm.invertOutletElev);
            this.formValidation.headOnPipe = false;
            this.formValidation.formErrors.headOnPipe = '';this.pressureFlowForm.headOnPipe = this.pressureFlowHeadOnPipeSnapshot;
        }
        await this.validateInput('pipe-diameter', this.pressureFlowForm.pipeDiameter);
        await this.validateInput('pipe-length', this.pressureFlowForm.pipeLength);
        await this.validateInput('mannings-n', this.pressureFlowForm.manningsN);
        await this.validateInput('entrance-loss-coeff', this.pressureFlowForm.entranceLossCoeff);
        await this.validateInput('bend-loss-coeff', this.pressureFlowForm.bendLossCoeff);
    }

    @action.bound async computePressureFlowInPipe() {
        this.disableComputeBtn = true;
        this.disablePrintBtn = true;
        MessageCenter.blockCalls(['Common_26']);
        await this.validateForm();

        if (this.validateRequiredInputs) {
            const pressureFlowPayload = this.pressureFlowForm
            const payload = {
                headDto: {
                    headOnPipe: parseFloat(pressureFlowPayload.headOnPipe),
                    pipeDiameter: parseFloat(pressureFlowPayload.pipeDiameter),
                    pipeLength: parseFloat(pressureFlowPayload.pipeLength),
                    manningsN: parseFloat(pressureFlowPayload.manningsN),
                    entranceLossCoeff: parseFloat(pressureFlowPayload.entranceLossCoeff),
                    bendLossCoeff: parseFloat(pressureFlowPayload.bendLossCoeff)
                },
                elevationDto: {
                    waterSurfaceElev: parseFloat(pressureFlowPayload.waterSurfaceElev),
                    invertOutletElev: parseFloat(pressureFlowPayload.invertOutletElev),
                    pipeDiameter: parseFloat(pressureFlowPayload.pipeDiameter),
                    pipeLength: parseFloat(pressureFlowPayload.pipeLength),
                    manningsN: parseFloat(pressureFlowPayload.manningsN),
                    entranceLossCoeff: parseFloat(pressureFlowPayload.entranceLossCoeff),
                    bendLossCoeff: parseFloat(pressureFlowPayload.bendLossCoeff)
                },
                specify: this.specifyControl === 'Head' ? true : false
            }
            try {
                const response = await HydraulicFormulaController.calculatePressureFlowPipeUsingPOST(payload);
                const shortCode = response?.data?.errorResponse?.errorCode?.replace(/[123]_[a-zA-Z]+_[a-zA-Z]+_/, '');
                if (response && response.data) {
                    if (response.data.validationPass) {
                        this.hasResults = true;
                        this.disablePrintBtn = false;
                        this.disableComputeBtn = false;
                        this.pressureFlowResults.flowQ = response.data?.flowQ;
                        this.pressureFlowResults.velocity = response.data?.velocity;
                        this.pressureFlowResults.maxAllowableFall = response.data?.maxFall;
                        this.pressureFlowResults.fractionCoefficient = response.data?.kp;
                        this.pressureFlowInputSnapshot = { ...this.pressureFlowForm };
                        this.specifyControlSnapshot = this.specifyControl;
                    } else if (
                        response?.data?.errorResponse?.errorMessage !== undefined &&
                        response?.data?.errorResponse?.errorType !== undefined
                    ) {
                        MessageCenter.resetMessageCenter();
                        MessageCenter.addNotification(
                            response?.data?.errorResponse?.errorMessage,
                            response?.data?.errorResponse?.errorType,
                            shortCode
                        );
                        this.hasResults = false;
                        this.disablePrintBtn = true;
                    }
                }
            } catch (error) {
                console.error(error);
            }
        } else {
            // If we are missing required inputs or they are invalid
            // If field validation has been added, input specific messages should not be shown at this time.
            MessageCenter.resetMessageCenter();
            this.hasResults = false;
            await MessageCenter.addNotificationFromCode('Common_26');
        }
        this.disableComputeBtn = false;
        this.isLoading = false;
        MessageCenter.unblockCalls();
    }

    @action.bound launchAndExport() {
        // disable print button
        this.disablePrintBtn = true;
        this.exportedInputs = [];
        this.exportedResults = [];

        for (const input in this.pressureFlowInputSnapshot) {
            if (this.specifyControlSnapshot === 'Head') {
                const propertiesToExclude = ['waterSurfaceElev', 'invertOutletElev']
                if (!propertiesToExclude.includes(input)) {
                    this.exportedInputs.push({
                        label: this.formLabelsForExport[input],
                        value: this.pressureFlowInputSnapshot[input]
                    })
                }
            } else {
                if (input !== 'headOnPipe') {
                    this.exportedInputs.push({
                        label: this.formLabelsForExport[input],
                        value: this.pressureFlowInputSnapshot[input]
                    })
                }
            }
        }
        for (const result in this.pressureFlowResults) {
            if (result !== 'errorResponse' || 'validationPass') {
                const label = this.resultLabelsForExport[result];
                const value = this.pressureFlowResults[result];
                if (label && value !== undefined) {
                    this.exportedResults.push({
                        label: this.resultLabelsForExport[result],
                        value: this.pressureFlowResults[result]
                    })
                }
            }
        }
        this.printFormula();
        this.disablePrintBtn = false;
    }

    @action.bound initializeDefaultValues(): void {
        const { elevationDto, headDto } = this.defaultValues
        if (this.specifyControl === 'Head') {
            this.pressureFlowForm = {
                headOnPipe: this.roundingHelperString(headDto.headOnPipe, 2),
                waterSurfaceElev: this.roundingHelperString(elevationDto.waterSurfaceElev, 2), //added these two values from elevationDto so that after the initial load if user switches raido button it is prefilled
                invertOutletElev: this.roundingHelperString(elevationDto.invertOutletElev, 2),
                pipeDiameter: this.roundingHelperString(headDto.pipeDiameter, 2),
                pipeLength: this.roundingHelperString(headDto.pipeLength, 2),
                manningsN: this.roundingHelperString(headDto.manningsN, 4),
                entranceLossCoeff: this.roundingHelperString(headDto.entranceLossCoeff, 2),
                bendLossCoeff: this.roundingHelperString(headDto.bendLossCoeff, 2)
            }
        } else {
            this.pressureFlowForm = {
                headOnPipe: this.roundingHelperString(headDto.headOnPipe, 2), //added this value from headDto so that onChange of initial radio button this value is prefilled
                waterSurfaceElev: this.roundingHelperString(elevationDto.waterSurfaceElev, 2),
                invertOutletElev: this.roundingHelperString(elevationDto.invertOutletElev, 2),
                pipeDiameter: this.roundingHelperString(elevationDto.pipeDiameter, 2),
                pipeLength: this.roundingHelperString(elevationDto.pipeLength, 2),
                manningsN: this.roundingHelperString(elevationDto.manningsN, 4),
                entranceLossCoeff: this.roundingHelperString(elevationDto.entranceLossCoeff, 2),
                bendLossCoeff: this.roundingHelperString(elevationDto.bendLossCoeff, 2)

            }
        }
        this.pressureFlowInputSnapshot = { ...this.pressureFlowForm }
        this.pressureFlowHeadOnPipeSnapshot = (this.pressureFlowForm.headOnPipe)
        this.pressureFlowWaterSurfaceElevSnapshot = (this.pressureFlowForm.waterSurfaceElev)
        this.specifyControlSnapshot = this.specifyControl
    }
}